import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { PageHeader } from '@/shared/ui/PageHeader'
import { Card } from '@/shared/ui/Card'
import { Modal } from '@/shared/ui/Modal'
import { Spinner } from '@/shared/ui/Spinner'
import { EmptyState } from '@/shared/ui/EmptyState'
import { Pagination } from '@/shared/ui/Pagination'
import { toast } from '@/store/useToastStore'
import { getApiError } from '@/api/client'
import { useAutoReassignIssues, useResolveAutoReassignIssue } from '@/api/hooks'
import { useDebounce } from '@/shared/hooks'
import { cn } from '@/shared/utils'
import { Search, UserCog, MapPin, Calendar, RefreshCcw, CheckCircle2, AlertTriangle } from 'lucide-react'

type StatusTab = 'open' | 'closed' | 'resolved' | 'trainer_changed' | 'booking_closed' | 'all'
const STATUS_TABS: StatusTab[] = ['open', 'closed', 'resolved', 'trainer_changed', 'booking_closed', 'all']

function statusCls(status?: string) {
  if (status === 'open')             return 'bg-amber-100 text-amber-700'
  if (status === 'resolved')         return 'bg-green-100 text-green-700'
  if (status === 'trainer_changed')  return 'bg-blue-100 text-blue-700'
  if (status === 'booking_closed')   return 'bg-gray-100 text-gray-600'
  if (status === 'closed')           return 'bg-gray-100 text-gray-600'
  return 'bg-gray-100 text-gray-600'
}
function reasonCls(reason?: string) {
  if (reason === 'no_match') return 'bg-red-50 text-red-600'
  if (reason === 'max_hops') return 'bg-purple-50 text-purple-600'
  return 'bg-gray-100 text-gray-600'
}

function fmtDT(v?: string | null) {
  if (!v) return '—'
  const d = new Date(String(v).replace(' ', 'T'))
  if (isNaN(d.getTime())) return v
  return `${d.toLocaleDateString('en-GB').replace(/\//g, '-')} ${d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: true })}`
}

// Field names aren't pinned down precisely by the API doc, so every accessor
// below tries the most likely shapes rather than assuming one exact schema.
function issueFields(row: any) {
  const bookingId = row.booking_id ?? row.booking?.id ?? row.id
  const city = row.city?.name ?? row.city_name ?? row.booking?.city?.name ?? row.booking?.city_name
  const studentName = row.student_name ?? row.student?.name ?? row.user_name ?? row.user?.name
  const studentPhone = row.student_phone ?? row.student?.phone ?? row.user_phone ?? row.user?.phone
  const lastTrainerName = row.last_trainer?.name ?? row.last_trainer_name
  const lastTrainerSchool = row.last_trainer?.school?.name ?? row.last_trainer?.vendor_name ?? row.last_trainer_school
  const currentTrainerName = row.current_trainer?.name ?? row.current_trainer_name ?? row.trainer?.name
  const trainerChanges = row.trainer_changes ?? 0
  const nextSessionAt = row.next_session_at
  const reason = row.reason
  const status = row.status
  const resolvedAt = row.resolved_at
  const resolvedByName = row.resolved_by?.name ?? row.resolved_by_name
  const resolutionNote = row.resolution_note ?? row.note
  return {
    bookingId, city, studentName, studentPhone, lastTrainerName, lastTrainerSchool,
    currentTrainerName, trainerChanges, nextSessionAt, reason, status, resolvedAt, resolvedByName, resolutionNote,
  }
}

export function AutoReassignIssuesPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [tab, setTab] = useState<StatusTab>('open')
  const [reasonFilter, setReasonFilter] = useState('')
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounce(search)
  const [page, setPage] = useState(1)
  const PER_PAGE = 20

  const { data, isLoading, isError } = useAutoReassignIssues({
    status: tab === 'all' ? undefined : tab,
    reason: reasonFilter || undefined,
    search: debouncedSearch || undefined,
    page, limit: PER_PAGE,
  })
  const rows: any[] = data?.data ?? []
  const total = data?.total ?? rows.length
  const lastPage = Math.max(1, Math.ceil(total / PER_PAGE))

  const resolve = useResolveAutoReassignIssue()
  const [resolveTarget, setResolveTarget] = useState<any | null>(null)
  const [note, setNote] = useState('')

  const openResolve = (row: any) => { setNote(''); setResolveTarget(row) }
  const confirmResolve = async () => {
    if (!resolveTarget) return
    const { bookingId } = issueFields(resolveTarget)
    try {
      await resolve.mutateAsync({ booking_id: bookingId, note: note.trim() || undefined })
      toast.success(t('autoReassignIssues.resolveSuccess'))
      setResolveTarget(null)
    } catch (e) { toast.error(getApiError(e)) }
  }

  return (
    <div className="space-y-5">
      <PageHeader
        title={t('autoReassignIssues.title')}
        subtitle={t('autoReassignIssues.subtitle')}
        breadcrumbs={[{ label: t('nav.dashboard'), to: '/dashboard' }, { label: t('autoReassignIssues.title') }]}
      />

      {/* ── Status tabs ── */}
      <div className="flex items-center gap-2 overflow-x-auto pb-0.5">
        {STATUS_TABS.map(tb => (
          <button key={tb} onClick={() => { setTab(tb); setPage(1) }}
            className={cn('flex items-center gap-1.5 h-9 px-4 rounded-full text-sm font-semibold border whitespace-nowrap transition-colors',
              tab === tb ? 'bg-brand-500 text-white border-brand-500' : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300')}>
            {t(`autoReassignIssues.status.${tb}`)}
          </button>
        ))}
      </div>

      {/* ── Filters ── */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={14} className="pointer-events-none absolute start-3 top-1/2 -translate-y-1/2 text-gray-400"/>
          <input value={search} onChange={e => { setSearch(e.target.value); setPage(1) }}
            placeholder={t('autoReassignIssues.searchPlaceholder')}
            className="w-full h-10 ps-9 pe-3 text-sm text-gray-700 placeholder-gray-400 bg-white border border-gray-200 rounded-xl focus:outline-none focus:border-brand-400 focus:ring-2 focus:ring-brand-400/30"/>
        </div>
        <select value={reasonFilter} onChange={e => { setReasonFilter(e.target.value); setPage(1) }}
          className="h-10 px-3 text-sm border border-gray-200 rounded-xl bg-white text-gray-600 focus:outline-none focus:border-brand-400 focus:ring-2 focus:ring-brand-400/30">
          <option value="">{t('autoReassignIssues.allReasons')}</option>
          <option value="no_match">{t('autoReassignIssues.reason.no_match')}</option>
          <option value="max_hops">{t('autoReassignIssues.reason.max_hops')}</option>
        </select>
      </div>

      <Card padding={false}>
        {isLoading ? (
          <div className="flex justify-center py-16"><Spinner className="w-8 h-8"/></div>
        ) : isError ? (
          <div className="text-center text-sm text-red-500 py-16 flex flex-col items-center gap-2">
            <AlertTriangle size={20}/>{t('autoReassignIssues.loadError')}
          </div>
        ) : rows.length === 0 ? (
          <EmptyState message={t('autoReassignIssues.emptyState')} icon={<RefreshCcw size={22}/>}/>
        ) : (
          <div className="overflow-x-auto">
            <table className="drivys-table w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th>{t('autoReassignIssues.colBooking')}</th>
                  <th>{t('autoReassignIssues.colStudent')}</th>
                  <th>{t('autoReassignIssues.colTrainers')}</th>
                  <th>{t('autoReassignIssues.colReason')}</th>
                  <th>{t('autoReassignIssues.colNextSession')}</th>
                  <th>{t('autoReassignIssues.colStatus')}</th>
                  <th className="text-end">{t('autoReassignIssues.colActions')}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(row => {
                  const f = issueFields(row)
                  return (
                    <tr key={f.bookingId} onClick={() => navigate(`/dashboard/booking/${f.bookingId}`)} className="cursor-pointer">
                      <td>
                        <div className="font-semibold text-gray-800">#{f.bookingId}</div>
                        {f.city && <div className="flex items-center gap-1 text-xs text-gray-400"><MapPin size={10}/>{f.city}</div>}
                      </td>
                      <td>
                        <div className="font-semibold text-gray-800">{f.studentName || '—'}</div>
                        <div className="text-xs text-gray-400">{f.studentPhone || '—'}</div>
                      </td>
                      <td>
                        <div className="flex items-center gap-1.5 text-xs text-gray-500">
                          <UserCog size={11} className="text-gray-400"/>
                          <span>{t('autoReassignIssues.lastTrainer')}: <span className="font-medium text-gray-700">{f.lastTrainerName || '—'}</span></span>
                        </div>
                        {f.lastTrainerSchool && <div className="text-[11px] text-gray-400 ms-[18px]">{f.lastTrainerSchool}</div>}
                        <div className="text-xs text-gray-500 mt-0.5">
                          {t('autoReassignIssues.current')}: <span className={cn('font-medium', f.currentTrainerName ? 'text-gray-700' : 'text-gray-400 italic')}>
                            {f.currentTrainerName || t('autoReassignIssues.unassigned')}
                          </span>
                        </div>
                        {f.trainerChanges > 0 && <div className="text-[11px] text-gray-400">{t('autoReassignIssues.trainerChanges', { count: f.trainerChanges })}</div>}
                      </td>
                      <td><span className={cn('px-2.5 py-0.5 rounded-full text-[12px] font-bold whitespace-nowrap', reasonCls(f.reason))}>{t(`autoReassignIssues.reason.${f.reason}`, String(f.reason ?? '—'))}</span></td>
                      <td className="text-gray-600 whitespace-nowrap"><span className="flex items-center gap-1"><Calendar size={11} className="text-gray-400"/>{fmtDT(f.nextSessionAt)}</span></td>
                      <td>
                        <span className={cn('px-2.5 py-0.5 rounded-full text-[12px] font-bold whitespace-nowrap', statusCls(f.status))}>{t(`autoReassignIssues.status.${f.status}`, String(f.status ?? '—'))}</span>
                        {f.status === 'resolved' && f.resolvedByName && (
                          <div className="text-[11px] text-gray-400 mt-0.5">{t('autoReassignIssues.resolvedBy', { name: f.resolvedByName })}</div>
                        )}
                      </td>
                      <td className="text-end">
                        {f.status === 'open' ? (
                          <button onClick={e => { e.stopPropagation(); openResolve(row) }}
                            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[11px] font-bold bg-green-500 text-white hover:bg-green-600">
                            <CheckCircle2 size={11}/>{t('autoReassignIssues.resolve')}
                          </button>
                        ) : <span className="text-gray-300 text-xs">—</span>}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}

        {total > PER_PAGE && (
          <Pagination page={page} lastPage={lastPage} total={total} perPage={PER_PAGE} onPageChange={setPage}/>
        )}
      </Card>

      {/* ── Resolve modal ── */}
      <Modal open={!!resolveTarget} onClose={() => setResolveTarget(null)} title={t('autoReassignIssues.resolveTitle')} size="sm">
        {resolveTarget && (
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              {t('autoReassignIssues.resolveConfirm', { id: issueFields(resolveTarget).bookingId })}
            </p>
            <div>
              <label className="text-xs font-semibold text-gray-500 mb-1 block">{t('autoReassignIssues.noteOptional')}</label>
              <textarea value={note} onChange={e => setNote(e.target.value.slice(0, 500))} rows={3}
                placeholder={t('autoReassignIssues.notePlaceholder')}
                className="w-full text-sm border border-gray-200 rounded-lg p-2.5 focus:outline-none focus:border-brand-400 focus:ring-2 focus:ring-brand-400/30 resize-none"/>
              <div className="text-[11px] text-gray-400 text-end mt-0.5">{note.length}/500</div>
            </div>
            <div className="flex items-center justify-end gap-3">
              <button onClick={() => setResolveTarget(null)} className="px-4 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50">{t('common.cancel')}</button>
              <button onClick={confirmResolve} disabled={resolve.isPending}
                className="px-4 py-2 text-sm rounded-lg text-white font-medium bg-green-500 hover:bg-green-600 disabled:opacity-50">
                {resolve.isPending ? t('autoReassignIssues.resolving') : t('autoReassignIssues.resolve')}
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  )
}
